import { ErrorBoundary } from "@sentry/react";
import _ from "lodash";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FETCH_USER_DETAILS } from "../../actions/actionTypes";
import { updateUserDetails } from "../../api";
import { IUserDetails } from "../../constants/interface";
import { ThemeContext } from "../../context";
import { ErrorMsg } from "../AppMessages";
import { getChangedFields } from "../../utils/getChangedFields";
import { BusinessFunctionInput, FormsToggleSwitch, TextInput } from "./common";
import { SubmitBtn } from "./common/Buttons";

interface Props {
  user: IUserDetails;
}

// Every field this form's own controls can actually change - unlike SettingsPage.tsx, `role` is a
// real editable TextInput here (not readonly), and `dark_mode` is part of this form's own draft/
// submit flow (not a separate always-immediate control like Settings' DarkLightToggle).
const EDITABLE_FIELDS = ["first_name", "last_name", "business_function", "department", "role", "dark_mode"] as const;

export const UserProfileForm = (props: Props) => {
  const dispatch = useDispatch();

  const { user } = props;

  const { setTheme } = useContext(ThemeContext);

  const [formKey, setFormKey] = useState(0);

  const [userDraft, setuserDraft] = useState(user);
  const [updateDisabled, setupdateDisabled] = useState(true);
  const [businessFunction, setBusinessFunction] = useState("");

  useEffect(() => {
    setupdateDisabled(_.isEqual(user, userDraft));
  }, [userDraft, user]);

  useEffect(() => {
    setuserDraft(user);
    setBusinessFunction(user.business_function ? user.business_function : "");
  }, [user]);

  useEffect(
    () => setuserDraft((prevState) => ({ ...prevState, business_function: businessFunction })),
    [businessFunction]
  );

  const handleUserProfileFormSubmit = useCallback(
    () =>
      updateUserDetails(user.email, getChangedFields(user, userDraft, EDITABLE_FIELDS)).then((response) => {
        if (typeof response !== "string")
          if ("error" in response) {
            toast.error(response.error);
          } else {
            toast.success("Profile updated successfull");
            if ("dark_mode" in response) response.dark_mode ? setTheme("dark") : setTheme("light");

            dispatch({ type: FETCH_USER_DETAILS, payload: response.email });
            setFormKey(formKey + 1);
          }
      }),
    [user, userDraft]
  );

  const handleInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = evt;
    setuserDraft({ ...userDraft, [name]: value });
  };

  const handleToggleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name },
    } = evt;

    setuserDraft((prevState) => ({ ...userDraft, [name]: !prevState[name] }));
  };

  return (
    <ErrorBoundary fallback={<ErrorMsg class={"formError"} />}>
      <form
        id="profile_form"
        className="profile_settings"
        name="profile_form"
        method="POST"
        data-test-id="profile_form"
        onSubmit={(event) => {
          event.preventDefault();
          return handleUserProfileFormSubmit();
        }}
        key={formKey}
      >
        <div className="profile_body">
          <div className="profile_section" id="myProfile">
            <div className="profile_section__header">
              <div className="profile_section__header--icon"></div>
              <div className="profile_section__header--title">MY PROFILE</div>
            </div>
            <div className="profile_section__body">
              <TextInput
                label="Firstname"
                name="first_name"
                value={userDraft.first_name}
                id={"firstname"}
                pattern="[a-zA-Z]+"
                onChange={handleInputChange}
              />
              <TextInput
                label="Lastname"
                name="last_name"
                value={userDraft.last_name}
                id={"lastname"}
                pattern="[a-zA-Z]+"
                onChange={handleInputChange}
              />
              <BusinessFunctionInput businessFunction={businessFunction} setBusinessFunction={setBusinessFunction} />
              <TextInput label="Department" name="department" onChange={handleInputChange} value={userDraft.department} id="department" />
              <TextInput label="Role" name="role" onChange={handleInputChange} value={userDraft.role} pattern="[a-zA-Z]+" id="role" />
              {/* <GeographicalScopeInput scope={scope} setScope={setScope} /> */}
            </div>
          </div>
          <div className="profile_section_gap" />

          <div className="profile_section" id="myPreferences">
            <div className="profile_section__header">
              <div className="profile_section__header--icon"></div>
              <div className="profile_section__header--title">MY PREFERENCES</div>
            </div>
            <div className="profile_section__body">
              {/* @ts-ignore */}
              <input type="text" name="dark_mode" id="dark_mode" value={userDraft.dark_mode} onChange={() => {}} hidden />
              <FormsToggleSwitch
                label="Theme"
                name="dark_mode"
                onChange={handleToggleChange}
                checked={userDraft.dark_mode}
                id="dark_mode"
                leftLabel={"Light"}
                rightLabel={"Dark"}
              />
            </div>
          </div>
        </div>
        <SubmitBtn id="updateProfile" value="update" disabled={updateDisabled} />
      </form>
    </ErrorBoundary>
  );
};
